import { PERCEPTION_MODELS } from 'constants/routePaths';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  AllProjectTranslationsQuery,
  ProductLookupByNamesQuery,
  ProjectNormSetByIdQuery,
  type ProductLookupRow,
  type ProjectNormSetRow,
} from '@graphql/queries/PerceptionModelQueries';
import { ArrowBack } from '@mui/icons-material';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';

import AnchorEditor from './AnchorEditor';
import { normSetMembers } from './normSetUtils';
import NormSetView from './NormSetView';
import PerceptionModelError from './PerceptionModelError';
import { sensorySignature } from './tokenProfile';
import TranslationSection from './TranslationSection';
import { PageHeader, PagePaper, PageTitle } from '../../styles/themeComponents';

/**
 * A saved norm set.
 *
 * A saved norm set, read only.
 *
 * Anchors define the comparison and every distance was measured from them, so
 * neither moves: a different comparison is a new norm set, not an edit of this
 * one. What does move is which products a translation targets — that lives on
 * the translation, where changing it leaves the norm set untouched.
 */

type Props = { normSetId: number };

const NormSetDetail: React.FC<Props> = ({ normSetId }) => {
  const history = useHistory();
  const producerId = useSelector((state) => selectWorkspaceProducerId(state));

  const { data, loading, error } = useQuery(ProjectNormSetByIdQuery, {
    variables: { id: normSetId, producerId },
    skip: !producerId,
  });
  // Scoped by client, so a norm set from another workspace comes back empty
  // and falls through to the "no norm set" state below. That read is also what
  // gates deletion: the delete button lives past this guard, so an id from
  // another workspace never reaches the mutation.
  const row: ProjectNormSetRow | null =
    data?.allPerceptionModelProjects?.nodes?.[0] ?? null;

  const members = React.useMemo(
    () => normSetMembers(row?.normSet ?? null),
    [row],
  );

  // Profiles for the members, so the set can be described by what it tastes
  // like rather than only by how many products it holds. A second read, but
  // the alternative is a page that names a hundred products and says nothing
  // about any of them.
  const { data: profileData } = useQuery(ProductLookupByNamesQuery, {
    variables: {
      jobId: row?.perceptionModelJobId,
      names: members.map((m) => m.product),
    },
    skip: !row || members.length === 0,
  });

  const memberRows: ProductLookupRow[] =
    profileData?.allPerceptionModelProductLookups?.nodes ?? [];

  // No anchor rows are loaded here, so there is nothing to call a token novel
  // against — see markNovel on NormSetView.
  const families = React.useMemo(
    () => sensorySignature(memberRows, []),
    [memberRows],
  );

  const [editingAnchors, setEditingAnchors] = React.useState(false);

  // Shares the cache entry TranslationSection already reads, so this is a
  // count rather than a second round trip.
  const { data: translationData } = useQuery(AllProjectTranslationsQuery, {
    variables: { projectId: row?.id },
    skip: !row,
  });

  const translationCount =
    translationData?.allPerceptionModelProjectTranslations?.nodes?.length ?? 0;

  if (loading || !producerId) return <PagePaper>Loading norm set…</PagePaper>;

  if (error) {
    return (
      <PagePaper>
        <PerceptionModelError error={error} />
      </PagePaper>
    );
  }

  if (!row) {
    return (
      <PagePaper>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => history.push(PERCEPTION_MODELS)}
            >
              Back
            </Button>
          }
        >
          No norm set #{normSetId}.
        </Alert>
      </PagePaper>
    );
  }

  return (
    <PagePaper>
      {/* One level up. Every page in this flow answers "how do I get back"
          the same way — see design-plans/perception-models-routing-audit.md. */}
      <Button
        size="small"
        startIcon={<ArrowBack fontSize="small" />}
        onClick={() => history.push(PERCEPTION_MODELS)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        Norm sets
      </Button>

      <PageHeader>Norm set</PageHeader>
      <PageTitle>{row.name}</PageTitle>

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Norm set #{row.id} · job {row.perceptionModelJobId} · created{' '}
        {new Date(row.createdDate).toLocaleDateString()} · {members.length}{' '}
        products
      </Typography>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <NormSetView
          jobId={row.perceptionModelJobId}
          anchors={row.anchors ?? []}
          members={members}
          families={families}
          markNovel={false}
          onChangeAnchors={() => setEditingAnchors(true)}
        />

        <AnchorEditor
          normSetId={row.id}
          jobId={row.perceptionModelJobId}
          producerId={producerId}
          anchors={row.anchors ?? []}
          members={members.map((m) => m.product)}
          translationCount={translationCount}
          open={editingAnchors}
          onClose={() => setEditingAnchors(false)}
        />

        {/* Belongs to this page, not to the view: it is a statement about a
            saved norm set's constraints, and would be false on the builder
            where nothing is fixed yet. */}
        <Alert severity="info">
          Membership is fixed once built: all {members.length} products were
          chosen under job {row.perceptionModelJobId} and a different set is a
          new norm set. The anchors can move — doing so re-measures every
          distance in the same write, so the numbers always describe a
          comparison that was actually made.
        </Alert>

        {/* Children. The norm set itself is immutable; translations are not
            part of it, so adding or removing one leaves it untouched. */}
        <TranslationSection projectId={row.id} />
      </Stack>
    </PagePaper>
  );
};

export default NormSetDetail;
